/* eslint-disable no-console */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, get } from 'lodash';
import { CircularProgress, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import history from 'common/utils/history';
import { StepProps } from 'common/types';
import { Box, Stepper } from 'components';
import { Logo } from 'assets';
import { RootState } from 'redux-modules/store/rootState';
import {
  getInvestorDetails,
  trackInvestmentProcess,
  getInvestmentDocument,
  emptyInvestmentDataState,
} from 'redux-modules/investor/Actions';

import styles from './styles';
import KYCDetails from './components/KYCDetails';
import SubscriptionForm from './components/SubscriptionForm';
import DocuSignView from './components/DocuSignView';
import WireInstruction from './components/WireInstruction';
import TradeConfirmation from './components/TradeConfirmation';

const typeSteps = [
  'KYC',
  'SUBSCRIPTION_FORM',
  'E_SIGN',
  'WIRE_INSTRUCTION',
  'TRADE_CONFIRMATION',
];

function getSteps() {
  return [
    { name: 'KYC', key: 'KYC_FORM' },
    { name: 'Amounts', key: 'SUBSCRIPTION_FORM' },
    { name: 'E-Signature', key: 'E_SIGN' },
    { name: 'Funds Transfer', key: 'WIRE_INSTRUCTION' },
    { name: 'Confirmation', key: 'TRADE_CONFIRMATION' },
  ];
}

function getStepContent(step: number, props: StepProps) {
  switch (step) {
    case 0:
      return <KYCDetails {...props} />;
    case 1:
      return <SubscriptionForm {...props} />;
    case 2:
      return <DocuSignView {...props} />;
    case 3:
      return <WireInstruction {...props} />;
    case 4:
      return <TradeConfirmation {...props} />;
    default:
      return <TradeConfirmation {...props} />;
  }
}

type Props = {
  selectedSPV: string;
  selectedDealId: any;
};

const InvestNow = ({ selectedSPV, selectedDealId }: Props) => {
  const dispatch = useDispatch();
  const classes = styles();

  const { latestDeal } = useSelector(({ Deals }: RootState) => Deals);

  const steps = getSteps();
  const pathName = useLocation().pathname;
  const search = history.location.search;
  const event = new URLSearchParams(search).get('event');
  const spv = selectedDealId || get(latestDeal, 'id');
  const [activeStep, setActiveStep] = React.useState(0);
  const [completedSteps, setCompletedSteps] = React.useState(0);
  const [stepName, setStepName] = React.useState<string>(typeSteps[activeStep]);
  // eslint-disable-next-line no-unused-vars
  const [allStepDisable, setAllStepDisable] = React.useState<boolean>(false);
  const [nextButtonClick, setNextButtonClick] = React.useState<boolean>(false);
  const [kycId, setKycId] = React.useState<string>('');

  const {
    investor,
    isLoading,
    errorMessage,
    investmentProcess,
    isProcessLoading,
  } = useSelector(({ Investor }: RootState) => Investor);

  const { investorKyc, investmentDetail } = investor;
  const kycDetail = investorKyc?.find((kyc: any) => kyc.spv === spv);
  const investmentData = investmentDetail?.find(
    (investment: any) => investment.spv === spv && investment.kycId === kycId,
  );

  // TODO: Api Call multiple time
  useEffect(() => {
    if (isEmpty(get(investor, 'id'))) {
      dispatch(getInvestorDetails());
    }
  }, [investor]);

  useEffect(() => {
    if (!isEmpty(get(kycDetail, 'id'))) setKycId(get(kycDetail, 'id'));
  }, [kycDetail]);

  useEffect(() => {
    if (!isEmpty(get(investmentData, 'id')) && isEmpty(investmentProcess)) {
      dispatch(
        trackInvestmentProcess({
          investmentDetailId: get(investmentData, 'id'),
        }),
      );
    }
  }, [investmentData]);

  useEffect(() => {
    if (get(investmentProcess, 'investmentId')) {
      dispatch(
        getInvestmentDocument({
          id: get(investmentProcess, 'investmentId'),
          type: 'NON_DOCU_SIGN_DOCUMENTS',
        }),
      );
    }
  }, [investmentProcess]);

  useEffect(() => {
    const sessionVal = sessionStorage.getItem('isDashboard');
    if (
      !event &&
      investmentProcess &&
      !isEmpty(investmentData) &&
      !isProcessLoading &&
      !nextButtonClick
    ) {
      if (get(investmentProcess, 'wireInstructionUploaded')) {
        sessionVal ? setActiveStep(0) : setActiveStep(4);
        setCompletedSteps(5);
        sessionVal ? setStepName(typeSteps[0]) : setStepName(typeSteps[4]);
      } else if (
        get(investmentProcess, 'wireReceiptUploaded') ||
        !isEmpty(get(investmentData, 'transactionId'))
      ) {
        sessionVal ? setActiveStep(0) : setActiveStep(4);
        setCompletedSteps(4);
        sessionVal ? setStepName(typeSteps[0]) : setStepName(typeSteps[4]);
      } else if (get(investmentProcess, 'docuSignDocumentSigned')) {
        sessionVal ? setActiveStep(0) : setActiveStep(3);
        setCompletedSteps(3);
        sessionVal ? setStepName(typeSteps[0]) : setStepName(typeSteps[3]);
      } else if (get(investmentProcess, 'subscriptionFormAdded')) {
        sessionVal ? setActiveStep(0) : setActiveStep(2);
        setCompletedSteps(2);
        sessionVal ? setStepName(typeSteps[0]) : setStepName(typeSteps[2]);
      } else if (get(investmentProcess, 'kycAdded')) {
        sessionVal ? setActiveStep(0) : setActiveStep(1);
        setCompletedSteps(1);
        sessionVal ? setStepName(typeSteps[0]) : setStepName(typeSteps[1]);
      }
    }
  }, [investmentProcess, investmentData, isProcessLoading, nextButtonClick]);

  const handleStepClick = (index: number) => {
    setActiveStep(index);
    setStepName(typeSteps[index]);
  };

  const handleNext = () => {
    setNextButtonClick(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (!get(investmentProcess, 'wireInstructionUploaded'))
      setCompletedSteps(activeStep + 1);
    setStepName(typeSteps[activeStep + 1]);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setStepName(typeSteps[activeStep - 1]);
  };

  return (
    <Box
      className={
        pathName === '/invest-now'
          ? classes.investNowLayout
          : classes.investNowLPopup
      }
    >
      <Box className={classes.investNowLayoutLeft}>
        <Box className={classes.logoInBox}>
          <img src={Logo} alt={Logo} className={classes.invLogoImg} />
        </Box>
        <Stepper
          activeStep={activeStep}
          completedSteps={completedSteps}
          steps={steps}
          orientation={true}
          handleStepClick={handleStepClick}
          allStepDisable={allStepDisable}
        />
      </Box>
      <Box
        className={
          pathName === '/invest-now'
            ? classes.investNowLayoutRight
            : classes.investNowLRPopup
        }
      >
        {/* <Box className={classes.investNowLayoutRightBox}>
          <Box className={classes.investNowLayoutRightCont}> */}
        {isLoading && (
          <Box className={classes.loader}>
            <CircularProgress color="inherit" size={40} />
          </Box>
        )}
        {errorMessage && (
          <Typography variant="body1" className={classes.errorMessage}>
            {errorMessage}
          </Typography>
        )}
        {!isLoading &&
          !isProcessLoading &&
          !isEmpty(get(investor, 'id')) &&
          !isEmpty(spv) && (
            <>
              {getStepContent(activeStep, {
                handleNext: handleNext,
                handleBack: handleBack,
                stepName,
                spv,
                investorId: get(investor, 'id'),
                investor: investor,
                kycId,
                kycDetail,
              })}
            </>
          )}
        <Typography className={classes.investNowTextBox}>
          You are investing in 8vdX SPV for YC{' '}
          {selectedSPV || get(latestDeal, 'deal')}.
        </Typography>
        {pathName === '/invest-now' && (
          <Typography
            variant="h6"
            className={classes.closeIcon}
            onClick={() => {
              dispatch(emptyInvestmentDataState());
              history.push('/dashboard');
            }}
          >
            <CloseIcon />
          </Typography>
        )}
      </Box>
    </Box>
  );
};
export default InvestNow;
